<script setup lang="ts">
/**
 * The cookie preferences dialog: one card per category with a toggle (the
 * necessary one is always on) and, under it, the table of every cookie or
 * storage entry in that category. A category with nothing in it says so. It
 * opens from the banner's "Choose" and from the footer and Settings links
 * (`consent.settingsOpen`), and "Save choices" stores the toggles as a custom
 * decision. Owns the dialog; the choice is `consent/consent.ts`.
 */
import { reactive, watch } from 'vue';

import { useI18n } from '@/i18n';
import { CONSENT_CATEGORIES, consent, type OptionalCategory } from '@/consent/consent';
import { COOKIE_TABLE } from '@/consent/cookie-table';
import GameWindow from '../kit/GameWindow.vue';
import ActionButton from '../panel/ActionButton.vue';
import Check from '../panel/Check.vue';
import SectionCard from '../panel/SectionCard.vue';
import SheetTransition from '../panel/SheetTransition.vue';

defineEmits<{ policy: [] }>();
const { t } = useI18n();
const draft = reactive<Record<OptionalCategory, boolean>>({ preferences: false, analytics: false, marketing: false });

// Start from the stored choice every time the dialog opens.
watch(
  () => consent.settingsOpen.value,
  (open) => {
    if (!open) return;
    const c = consent.record.value?.choices;
    draft.preferences = c?.preferences ?? false;
    draft.analytics = c?.analytics ?? false;
    draft.marketing = c?.marketing ?? false;
  },
  { immediate: true },
);

function close(): void {
  consent.settingsOpen.value = false;
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return;
  event.stopPropagation();
  close();
}
</script>

<template>
  <Teleport to="body">
    <SheetTransition>
      <GameWindow
        v-if="consent.settingsOpen.value"
        width="46rem"
        level="dialog"
        aria-modal="true"
        data-test="cookie-settings"
        :title="t('consent.settingsTitle')"
        icon="cog"
        @close="close"
        @keydown="onKeydown"
      >
        <p class="lead">
          {{ t('consent.settingsLead') }}
          <button type="button" class="link" @click="$emit('policy')">{{ t('consent.policy') }}</button>
        </p>

        <div class="cats">
          <SectionCard v-for="cat in CONSENT_CATEGORIES" :key="cat" :title="t(`consent.cat.${cat}.name`)" :data-consent-category="cat">
            <template #chip>
              <span v-if="cat === 'necessary'" class="k-chip state">{{ t('consent.alwaysOn') }}</span>
              <span v-else-if="!COOKIE_TABLE[cat].length" class="k-chip state" :data-consent-none="cat">{{ t('consent.noneUsed') }}</span>
            </template>
            <p class="desc">{{ t(`consent.cat.${cat}.desc`) }}</p>
            <Check v-if="cat !== 'necessary'" v-model="draft[cat]" :data-test="`consent-toggle-${cat}`">{{ t(`consent.cat.${cat}.name`) }}</Check>
            <details v-if="COOKIE_TABLE[cat].length" class="k-well items">
              <summary>{{ t('consent.details', { count: COOKIE_TABLE[cat].length }) }}</summary>
              <ul class="rows">
                <li v-for="c in COOKIE_TABLE[cat]" :key="c.name" class="row" :data-cookie-row="c.name">
                  <code class="name">{{ c.name }}</code>
                  <span class="k-label kind">{{ t(`consent.kind.${c.kind}`) }}</span>
                  <span class="purpose">{{ t(`consent.items.${c.key}.purpose`) }} {{ t(`consent.items.${c.key}.duration`) }}</span>
                </li>
              </ul>
            </details>
          </SectionCard>
        </div>

        <template #actions>
          <ActionButton size="sm" data-test="consent-modal-reject" @click="consent.rejectAll()">{{ t('consent.rejectAll') }}</ActionButton>
          <ActionButton size="sm" data-test="consent-modal-accept" @click="consent.acceptAll()">{{ t('consent.acceptAll') }}</ActionButton>
          <ActionButton variant="primary" size="sm" data-test="consent-save" @click="consent.save({ ...draft }, 'custom')">{{ t('consent.save') }}</ActionButton>
        </template>
      </GameWindow>
    </SheetTransition>
  </Teleport>
</template>

<style scoped>
.lead,
.desc {
  margin: 0;
  font-family: var(--font-game-body);
  font-size: 0.88rem;
  font-weight: 500;
  line-height: 1.4;
  color: #dde6ee;
}

.lead {
  margin-bottom: 10px;
}

.link {
  min-height: 44px;
  padding: 0 2px;
  border: 0;
  background: none;
  font: inherit;
  font-weight: 700;
  color: var(--kit-gold);
  text-decoration: underline;
  cursor: pointer;
  touch-action: manipulation;
}

.cats {
  display: grid;
  gap: 12px;
}

.state {
  padding-left: 0.6rem;
  font-size: 0.8rem;
}

.items {
  padding: 4px 10px;
}

.items summary {
  display: flex;
  align-items: center;
  min-height: 44px;
  font-family: var(--font-game);
  font-size: 0.85rem;
  cursor: pointer;
}

.rows {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0 0 10px;
  list-style: none;
}

/* A phone has no width for a table: each entry is a short stacked row. */
.row {
  display: grid;
  gap: 2px 10px;
  grid-template-columns: minmax(0, 1fr) auto;
  font-family: var(--font-game-body);
  font-size: 0.8rem;
  line-height: 1.35;
  color: #dde6ee;
}

.name {
  overflow-wrap: anywhere;
  font-weight: 700;
}

.kind {
  font-size: 0.72rem;
}

.purpose {
  grid-column: 1 / -1;
  color: #b8c7d6;
}
</style>
